import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.sonitrix.com';
const LANGUAGES = ['fr', 'en', 'de'];

const HOME_META = {
  fr: {
    title: 'Assistant vocal IA pour les appels des PME | Sonitrix',
    description: 'Sonitrix répond aux appels des PME, qualifie les demandes et aide à organiser les rendez-vous grâce à un assistant vocal IA.',
    locale: 'fr_FR',
  },
  en: {
    title: 'AI Voice Assistant for Small Business Calls | Sonitrix',
    description: 'Sonitrix helps small businesses answer calls, qualify requests and organize appointments with an AI voice assistant.',
    locale: 'en_US',
  },
  de: {
    title: 'KI-Telefonassistent für KMU-Anrufe | Sonitrix',
    description: 'Sonitrix unterstützt KMU bei Anrufen, qualifiziert Anfragen und hilft mit einem KI-Telefonassistenten bei der Terminplanung.',
    locale: 'de_DE',
  },
};

const normalizePath = (path) => path === '/' ? '/' : `/${path.split('/').filter(Boolean).join('/')}/`;

const SeoHead = ({ lang = 'en', path = '', title, description, noindex = false, faqItems = [], canonicalUrl, alternates = true }) => {
  const currentLang = LANGUAGES.includes(lang) ? lang : 'en';
  const localizedPath = normalizePath(`/${currentLang}/${path}`);
  const canonical = canonicalUrl || `${SITE_URL}${localizedPath}`;
  const meta = HOME_META[currentLang];
  const pageTitle = title || meta.title;
  const pageDescription = description || meta.description;
  const alternatePath = path ? `/${path}` : '';

  const structuredData = noindex ? [] : [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Sonitrix',
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo512.png`,
      email: 'contact@sonitrix.com',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: 'Sonitrix',
      url: `${SITE_URL}/`,
      inLanguage: LANGUAGES,
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Sonitrix',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: canonical,
      description: pageDescription,
      provider: { '@id': `${SITE_URL}/#organization` },
    },
    ...(faqItems.length ? [{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    }] : []),
  ];

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      {!noindex && <link rel="canonical" href={canonical} />}
      {!noindex && alternates && LANGUAGES.map((code) => (
        <link key={code} rel="alternate" hrefLang={code} href={`${SITE_URL}${normalizePath(`/${code}${alternatePath}`)}`} />
      ))}
      {!noindex && alternates && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${normalizePath(`/en${alternatePath}`)}`} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Sonitrix" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={meta.locale} />
      <meta property="og:image" content={`${SITE_URL}/images/sonitrix.jpg`} />
      <meta property="og:image:alt" content="Sonitrix" />
      <meta name="twitter:card" content="summary_large_image" />
      {structuredData.map((data, index) => (
        <script key={index} type="application/ld+json">{JSON.stringify(data)}</script>
      ))}
    </Helmet>
  );
};

export { HOME_META, LANGUAGES };
export default SeoHead;
